import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Users, 
  BookOpen, 
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle2,
  ArrowUpRight,
  Bot,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { getAnalyticsOverview, getConversations, seedData } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, conversationsRes] = await Promise.all([
        getAnalyticsOverview(),
        getConversations(null, 5)
      ]);
      setStats(analyticsRes.data);
      setRecentConversations(conversationsRes.data);
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      setSeeding(true);
      const res = await seedData();
      if (res.data.status === 'seeded') {
        toast.success('Knowledge base seeded successfully!');
        loadDashboardData();
      } else {
        toast.info('Knowledge base already has data');
      }
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const statCards = [
    {
      title: 'Total Conversations',
      value: stats?.conversations?.total || 0,
      icon: MessageSquare,
      trend: '+12%',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      title: 'Active Chats',
      value: stats?.conversations?.active || 0,
      icon: Users,
      trend: stats?.conversations?.active > 0 ? 'Live' : 'None',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10'
    },
    {
      title: 'Messages Today',
      value: stats?.messages?.today || 0,
      icon: TrendingUp,
      trend: '+8%',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Knowledge Articles',
      value: stats?.knowledge_base?.articles || 0,
      icon: BookOpen,
      trend: `${stats?.knowledge_base?.categories || 0} categories`,
      color: 'text-violet-500',
      bgColor: 'bg-violet-500/10'
    }
  ];

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="dashboard-loading">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-card/50 border-border/60">
              <CardContent className="p-6">
                <div className="h-20 skeleton rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Monitor your WhatsApp support bot performance
          </p>
        </div>
        <Button 
          onClick={handleSeedData} 
          disabled={seeding}
          className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow"
          data-testid="seed-data-btn"
        >
          <Zap className="w-4 h-4 mr-2" />
          {seeding ? 'Seeding...' : 'Seed Sample Data'}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="stat-card bg-card/50 border-border/60 hover:border-primary/30"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className={cn("p-2 rounded-lg", stat.bgColor)}>
                  <stat.icon className={cn("w-5 h-5", stat.color)} />
                </div>
                <Badge variant="secondary" className="font-mono text-xs">
                  {stat.trend}
                </Badge>
              </div>
              <div className="mt-4">
                <p className="text-3xl font-bold tracking-tight">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Conversations */}
        <Card className="bg-card/50 border-border/60">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Recent Conversations
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/conversations')}
              data-testid="view-all-conversations"
            >
              View All
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              {recentConversations.length > 0 ? (
                <div className="space-y-3">
                  {recentConversations.map((conv) => (
                    <div
                      key={conv.id}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/conversations/${conv.id}`)}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">
                            {conv.customer_name || conv.phone_number}
                          </p>
                          <div className={cn(
                            "status-dot flex-shrink-0",
                            conv.status === 'active' && "status-active",
                            conv.status === 'resolved' && "status-resolved",
                            conv.status === 'escalated' && "status-escalated"
                          )} />
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {conv.phone_number}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-muted-foreground">
                          {formatTime(conv.last_message_at)}
                        </p>
                        <Badge variant="secondary" className="mt-1 text-xs">
                          {conv.message_count} msgs
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <MessageSquare className="w-12 h-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No conversations yet</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Messages will appear here when users start chatting
                  </p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Quick Stats & Actions */}
        <div className="space-y-6">
          {/* Bot Status */}
          <Card className="bg-card/50 border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Bot Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-emerald-500/20 flex items-center justify-center animate-pulse-gold">
                  <Bot className="w-8 h-8 text-emerald-500" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-emerald-500">Online</p>
                  <p className="text-sm text-muted-foreground">
                    Ready to handle WhatsApp messages
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>Response Time</span>
                  </div>
                  <p className="text-lg font-semibold mt-1">&lt; 5 sec</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Resolution Rate</span>
                  </div>
                  <p className="text-lg font-semibold mt-1">95%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-card/50 border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="secondary" 
                className="w-full justify-start"
                onClick={() => navigate('/knowledge')}
                data-testid="quick-action-knowledge"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Manage Knowledge Base
              </Button>
              <Button 
                variant="secondary" 
                className="w-full justify-start"
                onClick={() => navigate('/troubleshooting')}
                data-testid="quick-action-troubleshooting"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Edit Troubleshooting Flows
              </Button>
              <Button 
                variant="secondary" 
                className="w-full justify-start"
                onClick={() => navigate('/settings')}
                data-testid="quick-action-settings"
              >
                <Bot className="w-4 h-4 mr-2" />
                Configure Bot Settings
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
